import React, { useEffect, useState, useCallback } from 'react';
import './VideoStudioPanel.css';
import {
  calculateDialogueDurationSeconds,
  type VideoProject,
  type VideoScene,
  type VideoReference,
  type VideoAudioSettings as AudioSettingsType,
} from './videoStudioTypes';
import { videoStudioApi } from './videoStudioApi';
import { VideoSourcePreview } from './VideoSourcePreview';
import { VideoReferencesEditor } from './VideoReferencesEditor';
import { VideoScenesEditor } from './VideoScenesEditor';
import { VideoAudioSettings } from './VideoAudioSettings';
import { VideoReviewPanel } from './VideoReviewPanel';
import { VideoVersionsPanel } from './VideoVersionsPanel';
import { VideoTrimmerModal } from './VideoTrimmerModal';
import { stitchVideoScenes, type StitchTransitionType } from './videoStitcher';

interface VideoStudioPanelProps {
  isOpen: boolean;
  onClose?: () => void;
  campaignId: string;
  creativeId: string;
  creativeTitle?: string;
  onVideoApplied?: (videoUrl: string) => void;
  embedded?: boolean;
}

type TabKey = 'references' | 'scenes' | 'audio' | 'review' | 'versions';

export const VideoStudioPanel: React.FC<VideoStudioPanelProps> = ({
  isOpen,
  onClose,
  campaignId,
  creativeId,
  creativeTitle,
  onVideoApplied,
  embedded = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('scenes');
  const [project, setProject] = useState<VideoProject | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [generatingSceneOrder, setGeneratingSceneOrder] = useState<number | null>(null);
  const [uploadingSceneOrder, setUploadingSceneOrder] = useState<number | null>(null);
  const [narratingSceneOrder, setNarratingSceneOrder] = useState<number | null>(null);
  const [trimmerScene, setTrimmerScene] = useState<VideoScene | null>(null);
  const [isStitching, setIsStitching] = useState(false);
  const [stitchProgress, setStitchProgress] = useState<string | null>(null);

  const normalizeProjectTiming = (rawProject: VideoProject): VideoProject => {
    const normalizedScenes = (rawProject.scenes || []).map((s) => {
      const hasDialogue = Boolean(s.dialogue && s.dialogue.trim());
      const calculated = hasDialogue
        ? calculateDialogueDurationSeconds(s.dialogue, s.duration_seconds || 5)
        : (Number(s.duration_seconds) || 5);
      return {
        ...s,
        duration_seconds: calculated,
      };
    });
    const totalDuration = normalizedScenes.reduce(
      (acc, s) => acc + (Number(s.duration_seconds) || 0),
      0
    );
    return {
      ...rawProject,
      scenes: normalizedScenes,
      target_duration_seconds: totalDuration > 0 ? totalDuration : (rawProject.target_duration_seconds || 15),
    };
  };

  const fetchProject = useCallback(async () => {
    if (!campaignId || !creativeId) return;
    setLoading(true);
    try {
      const res = await videoStudioApi.getVideoProject(campaignId, creativeId);
      if (res.ok && res.project) {
        setProject(normalizeProjectTiming(res.project));
      }
    } catch (err) {
      console.error('[VideoStudio] Falha ao carregar projeto:', err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, creativeId]);

  useEffect(() => {
    if (isOpen) fetchProject();
  }, [isOpen, fetchProject]);

  if (!isOpen) return null;

  const persistProjectState = async (currentProj?: VideoProject | null): Promise<VideoProject | null> => {
    const base = currentProj || project;
    if (!base) return null;

    const normalized = normalizeProjectTiming(base);
    setProject(normalized);

    const [, scenesRes] = await Promise.all([
      videoStudioApi.updateVideoProject(campaignId, creativeId, {
        aspect_ratio: normalized.aspect_ratio,
        target_duration_seconds: normalized.target_duration_seconds,
        script_goal: normalized.script_goal,
        script_hook: normalized.script_hook,
        script_body: normalized.script_body,
        script_cta: normalized.script_cta,
        style_notes: normalized.style_notes,
        audio_settings: normalized.audio_settings,
      }),
      videoStudioApi.updateScenes(campaignId, creativeId, normalized.scenes || []),
      videoStudioApi.updateReferences(campaignId, creativeId, normalized.references || []),
    ]);

    const syncedProject: VideoProject = {
      ...normalized,
      scenes: scenesRes?.scenes || normalized.scenes,
    };
    setProject(syncedProject);

    return syncedProject;
  };

  const handleSaveAll = async () => {
    if (!project) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      await persistProjectState(project);
      setSaveStatus('✓ Alterações e durações salvas com sucesso!');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err: any) {
      setSaveStatus(`Erro ao salvar: ${err.message || 'Falha de rede'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleScenesChange = (scenes: VideoScene[]) => {
    if (!project) return;
    const totalDuration = scenes.reduce((acc, s) => acc + (Number(s.duration_seconds) || 0), 0);
    setProject({ ...project, scenes, target_duration_seconds: totalDuration > 0 ? totalDuration : project.target_duration_seconds });
  };

  const handleGenerateSingleScene = async (sceneOrder: number) => {
    if (!campaignId || !creativeId || !project) return;
    setGeneratingSceneOrder(sceneOrder);
    setSaveStatus(`Salvando alterações e tempo recalculado da Cena #${sceneOrder}...`);
    try {
      const savedProject = await persistProjectState(project);
      const targetScene = (savedProject?.scenes || project.scenes || []).find(
        (s) => s.scene_order === sceneOrder
      );
      const sceneDuration = targetScene?.duration_seconds || 5;

      setSaveStatus(`Renderizando Cena #${sceneOrder} (${sceneDuration}s) no Google Veo 3.1 com continuidade...`);
      const res = await videoStudioApi.generateWithVeo(campaignId, creativeId, {
        scene_order: sceneOrder,
        model_version: 'veo-3.1-fast',
        duration_seconds: sceneDuration,
        dialogue: targetScene?.dialogue,
        scenes: savedProject?.scenes || project.scenes,
      });
      if (res.ok && res.video_url) {
        setSaveStatus(`✓ Cena #${sceneOrder} (${sceneDuration}s) gerada com sucesso!`);
        if (onVideoApplied) onVideoApplied(res.video_url);
        await fetchProject();
      } else {
        setSaveStatus(`Falha na renderização: ${res.message || 'Erro'}`);
      }
    } catch (err: any) {
      setSaveStatus(`Erro: ${err.message || 'Falha de conexão'}`);
    } finally {
      setGeneratingSceneOrder(null);
    }
  };

  const handleApproveScene = async (sceneOrder: number, isApproved: boolean) => {
    if (!campaignId || !creativeId || !project) return;
    const newStatus = isApproved ? 'approved' : 'generated';
    const updatedScenes = (project.scenes || []).map(s => s.scene_order === sceneOrder ? { ...s, status: newStatus as any } : s);
    setProject({ ...project, scenes: updatedScenes });
    try {
      await videoStudioApi.updateSceneStatus(campaignId, creativeId, sceneOrder, newStatus);
      setSaveStatus(isApproved ? `✓ Cena #${sceneOrder} aprovada!` : `Aprovação desmarcada.`);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err: any) {
      console.warn('[VideoStudio] Falha ao atualizar status:', err);
    }
  };

  const handleSelectSceneGeneration = async (
    sceneOrder: number,
    videoUrl: string,
    durationSeconds?: number | null
  ) => {
    if (!campaignId || !creativeId || !project) return;
    const updatedScenes = (project.scenes || []).map((s) =>
      s.scene_order === sceneOrder
        ? {
            ...s,
            video_url: videoUrl,
            status: 'approved' as const,
            duration_seconds: Number(durationSeconds || s.duration_seconds) || 5,
          }
        : s
    );
    setProject({ ...project, scenes: updatedScenes });
    try {
      await videoStudioApi.updateSceneStatus(campaignId, creativeId, sceneOrder, 'approved', {
        video_url: videoUrl,
        ...(durationSeconds ? { duration_seconds: Number(durationSeconds) } : {}),
      });
      setSaveStatus(`✓ Geração selecionada para a Cena #${sceneOrder}!`);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err: any) {
      console.warn('[VideoStudio] Falha ao vincular geração à cena:', err);
    }
  };

  const handleSelectOfficialVersion = async (versionId: string, videoUrl: string) => {
    if (!campaignId || !creativeId || !project) return;
    try {
      const res = await videoStudioApi.selectVideoVersion(campaignId, creativeId, versionId);
      if (res.ok) {
        if (onVideoApplied) onVideoApplied(videoUrl);
        setSaveStatus('✓ Vídeo principal da campanha atualizado!');
        await fetchProject();
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (err: any) {
      setSaveStatus(`Erro ao selecionar versão: ${err.message || 'Falha'}`);
    }
  };

  const handleUploadSceneVideo = async (sceneOrder: number, file: File) => {
    if (!campaignId || !creativeId) return;
    setUploadingSceneOrder(sceneOrder);
    setSaveStatus(`Enviando vídeo para a Cena #${sceneOrder}...`);
    try {
      const res = await videoStudioApi.uploadSceneVideo(campaignId, creativeId, sceneOrder, file);
      if (res.ok && res.video_url) {
        setSaveStatus(`✓ Vídeo da Cena #${sceneOrder} enviado e aprovado!`);
        if (onVideoApplied) onVideoApplied(res.video_url);
        await fetchProject();
        setTimeout(() => setSaveStatus(null), 3500);
      } else {
        setSaveStatus(`Falha no upload: ${res.message || 'Erro'}`);
      }
    } catch (err: any) {
      setSaveStatus(`Erro: ${err.message || 'Falha de rede'}`);
    } finally {
      setUploadingSceneOrder(null);
    }
  };

  const handleNarrateScene = async (sceneOrder: number) => {
    if (!campaignId || !creativeId || !project) return;
    setNarratingSceneOrder(sceneOrder);
    setSaveStatus(`Salvando fala e sintetizando voz em português para a Cena #${sceneOrder}...`);
    try {
      const savedProject = await persistProjectState(project);
      const targetScene = (savedProject?.scenes || project.scenes || []).find(
        (s) => s.scene_order === sceneOrder
      );
      const res = await videoStudioApi.narrateScene(
        campaignId,
        creativeId,
        sceneOrder,
        targetScene?.dialogue
      );
      if (res.ok && res.narration_audio_url) {
        setSaveStatus(`✓ Voz da Cena #${sceneOrder} sintetizada com sucesso!`);
        await fetchProject();
        setTimeout(() => setSaveStatus(null), 3500);
      } else {
        setSaveStatus(`Falha na sintetização de voz: ${res.message || 'Erro desconhecido'}`);
      }
    } catch (err: any) {
      setSaveStatus(`Erro ao sintetizar voz: ${err.message || 'Falha de rede'}`);
    } finally {
      setNarratingSceneOrder(null);
    }
  };

  const handleStitchScenes = async (transitionType: StitchTransitionType = 'crossfade') => {
    if (!campaignId || !creativeId || !project) return;
    const ready = (project.scenes || []).filter(s => Boolean(s.video_url));
    if (ready.length < 2) return;
    setIsStitching(true);
    setStitchProgress('Unindo cenas com transição suave...');
    try {
      const blob = await stitchVideoScenes(
        project.scenes || [],
        project.aspect_ratio || '9:16',
        (i) => setStitchProgress(i.message),
        { transitionType, transitionDurationSeconds: 0.5 }
      );
      setStitchProgress('Salvando vídeo consolidado...');
      const res = await videoStudioApi.stitchScenes(campaignId, creativeId, blob);
      if (res.ok && res.video_url) {
        setSaveStatus(`✓ Todas as ${ready.length} cenas foram unidas com sucesso!`);
        if (onVideoApplied) onVideoApplied(res.video_url);
        await fetchProject();
        setTimeout(() => setSaveStatus(null), 4000);
      } else {
        setSaveStatus(`Falha ao salvar vídeo: ${res.message || 'Erro'}`);
      }
    } catch (err: any) {
      setSaveStatus(`Erro ao unir: ${err.message || 'Falha de processamento'}`);
    } finally {
      setIsStitching(false);
      setStitchProgress(null);
    }
  };

  const content = (
    <div className={`vstudio-container ${embedded ? 'vstudio-container-embedded' : ''}`} onClick={(e) => e.stopPropagation()}>
      <div className="vstudio-header">
        <div className="vstudio-title-group">
          <h2>🎬 Estúdio de Vídeo para Anúncios</h2>
          <p>Criativo: <strong>{creativeTitle || creativeId}</strong> • Modo Flow Pilot</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            className="vstudio-btn"
            style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', padding: '0.45rem 0.95rem', fontSize: '0.8rem', fontWeight: 700, border: 'none', cursor: 'pointer' }}
            onClick={async () => {
              if (project) {
                try {
                  await persistProjectState(project);
                } catch (_) {}
              }
              setActiveTab('versions');
            }}
          >
            🚀 Gerar com Google Veo
          </button>
          {!embedded && onClose && <button type="button" className="vstudio-close-btn" onClick={onClose}>✕</button>}
        </div>
      </div>

      <div className="vstudio-tabs">
        <button type="button" className={`vstudio-tab-btn ${activeTab === 'scenes' ? 'active' : ''}`} onClick={() => setActiveTab('scenes')}>
          📋 Roteiro & Cenas ({project?.scenes?.length || 0})
        </button>
        <button type="button" className={`vstudio-tab-btn ${activeTab === 'references' ? 'active' : ''}`} onClick={() => setActiveTab('references')}>
          🖼️ Referências Visuais ({project?.references?.length || 0})
        </button>
        <button type="button" className={`vstudio-tab-btn ${activeTab === 'audio' ? 'active' : ''}`} onClick={() => setActiveTab('audio')}>
          🎙️ Áudio & Narração
        </button>
        <button type="button" className={`vstudio-tab-btn ${activeTab === 'review' ? 'active' : ''}`} onClick={() => setActiveTab('review')}>
          📦 Revisão & Flow Export
        </button>
        <button type="button" className={`vstudio-tab-btn ${activeTab === 'versions' ? 'active' : ''}`} onClick={() => setActiveTab('versions')}>
          🎞️ Versões Renderizadas ({project?.versions?.length || 0})
        </button>
      </div>

      <div className="vstudio-body">
        {loading || !project ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary, #64748b)' }}>
            <div style={{ fontSize: '1.75rem', marginBottom: '0.75rem' }}>⏳</div>
            <p>Carregando especificações do estúdio de vídeo...</p>
          </div>
        ) : (
          <div className="vstudio-grid-2">
            <aside>
              <VideoSourcePreview
                project={project}
                onSelectOfficialVersion={handleSelectOfficialVersion}
                onSelectSceneGeneration={handleSelectSceneGeneration}
              />
            </aside>
            <main>
              {activeTab === 'scenes' && (
                <VideoScenesEditor
                  scenes={project.scenes || []}
                  versions={project.versions || []}
                  targetDuration={project.target_duration_seconds || 15}
                  onChange={handleScenesChange}
                  onGenerateScene={handleGenerateSingleScene}
                  onApproveScene={handleApproveScene}
                  onSelectSceneGeneration={handleSelectSceneGeneration}
                  onUploadSceneVideo={handleUploadSceneVideo}
                  onNarrateScene={handleNarrateScene}
                  onOpenTrimmer={(sc) => setTrimmerScene(sc)}
                  onStitchScenes={handleStitchScenes}
                  generatingSceneOrder={generatingSceneOrder}
                  uploadingSceneOrder={uploadingSceneOrder}
                  narratingSceneOrder={narratingSceneOrder}
                  isStitching={isStitching}
                  stitchProgressMessage={stitchProgress}
                />
              )}
              {activeTab === 'references' && (
                <VideoReferencesEditor references={project.references || []} onChange={(refs) => setProject({ ...project, references: refs })} />
              )}
              {activeTab === 'audio' && (
                <VideoAudioSettings project={project} onChangeProject={(patch) => setProject({ ...project, ...patch })} onChangeAudio={(a) => setProject({ ...project, audio_settings: a })} />
              )}
              {activeTab === 'review' && (
                <VideoReviewPanel
                  project={project}
                  onRefresh={fetchProject}
                  onBeforeExport={() => persistProjectState(project)}
                  onNavigateToVersions={async () => {
                    try {
                      await persistProjectState(project);
                    } catch (_) {}
                    setActiveTab('versions');
                  }}
                />
              )}
              {activeTab === 'versions' && (
                <VideoVersionsPanel
                  project={project}
                  onRefresh={fetchProject}
                  onBeforeGenerate={() => persistProjectState(project)}
                  onVersionSelected={(url) => { if (onVideoApplied) onVideoApplied(url); fetchProject(); }}
                />
              )}
            </main>
          </div>
        )}
      </div>

      <div className="vstudio-footer">
        <div>
          {saveStatus && (
            <span style={{ fontSize: '0.85rem', color: saveStatus.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
              {saveStatus}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {!embedded && onClose && (
            <button type="button" className="vstudio-btn vstudio-btn-secondary" onClick={onClose}>Fechar</button>
          )}
          <button type="button" className="vstudio-btn vstudio-btn-primary" onClick={handleSaveAll} disabled={saving || !project}>
            {saving ? 'Salvando...' : '💾 Salvar Alterações'}
          </button>
        </div>
      </div>

      {trimmerScene && (
        <VideoTrimmerModal
          campaignId={campaignId}
          creativeId={creativeId}
          scene={trimmerScene}
          aspectRatio={project?.aspect_ratio || '9:16'}
          onClose={() => setTrimmerScene(null)}
          onSuccess={(uploadedUrl) => {
            if (onVideoApplied && uploadedUrl) onVideoApplied(uploadedUrl);
            fetchProject();
            setSaveStatus(`✓ Trecho recortado e salvo na Cena #${trimmerScene.scene_order}!`);
            setTimeout(() => setSaveStatus(null), 3500);
          }}
        />
      )}
    </div>
  );

  return embedded ? content : <div className="vstudio-overlay" onClick={onClose}>{content}</div>;
};

export default VideoStudioPanel;
